<script setup lang="ts">
import { gql } from '@apollo/client/core';
import { useLazyQuery } from '@vue/apollo-composable';
import BulkAddToCurriculumDialog from './BulkAddToCurriculumDialog.vue';
import { formatCourseInstance } from '@/utils/courseInstance';

const GET_UNFLOATED = gql`
  query UnfloatedCourses($query: String, $first: Int!, $page: Int) {
    unfloatedCourses(query: $query, first: $first, page: $page) {
      data {
        id
        course_code
        course_title
        unit
        course_instance_id
        course_instance_year
      }
      paginatorInfo {
        currentPage
        lastPage
        perPage
        total
        hasMorePages
      }
    }
  }
`;

const search = ref('');
const page = ref(1);
const perPage = ref(50);

const variables = computed(() => ({
  query: search.value || undefined,
  first: perPage.value,
  page: page.value,
}));

const { result, loading, load, refetch } = useLazyQuery(GET_UNFLOATED, variables);
load();

const courses = computed(() => result.value?.unfloatedCourses?.data ?? []);
const paginatorInfo = computed(() => result.value?.unfloatedCourses?.paginatorInfo ?? null);
const totalRecords = computed(() => paginatorInfo.value?.total ?? 0);

const selectedRows = ref<Array<Record<string, unknown>>>([]);
const showBulkCurriculumDialog = ref(false);
const selectedForCurriculum = computed(() =>
  selectedRows.value.map(r => ({
    id: String(r.id), course_code: String(r.course_code),
    course_title: String(r.course_title), unit: Number(r.unit),
  })),
);

async function onCurriculumSuccess() {
  selectedRows.value = [];
  await refetch();
}

let debounce: ReturnType<typeof setTimeout> | null = null;
watch(search, () => {
  if (debounce) clearTimeout(debounce);
  debounce = setTimeout(() => {
    page.value = 1;
    refetch();
  }, 400);
});

function onPageChange(event: { page?: number; rows?: number }) {
  if (event.rows && event.rows !== perPage.value) {
    perPage.value = event.rows;
    page.value = 1;
  } else {
    page.value = (event.page ?? 0) + 1;
  }
  refetch();
}
</script>

<template>
  <div class="space-y-3">
    <div class="bg-yellow-50 text-yellow-800 p-3 rounded text-sm flex items-start gap-2">
      <i class="pi pi-info-circle mt-0.5"></i>
      <div>
        <p class="font-semibold">Unfloated Courses</p>
        <p>These courses exist in your school but are not assigned to any programme curriculum. To float a course, add it to a curriculum entry on the Curriculum tab.</p>
      </div>
    </div>

    <div class="flex items-center justify-between">
      <span class="text-sm text-gray-600">
        <strong>{{ totalRecords }}</strong> unfloated course(s)
      </span>
      <InputText v-model="search" placeholder="Search by code or title..." class="w-72" />
    </div>

    <div v-if="selectedRows.length > 0"
      class="bg-primary/10 border border-primary/20 rounded-lg p-3 flex items-center justify-between">
      <span class="text-sm text-primary-700 font-medium">{{ selectedRows.length }} course(s) selected</span>
      <div class="flex gap-2">
        <Button label="Add to Curriculum" icon="pi pi-plus-circle" size="small"
          @click="showBulkCurriculumDialog = true" />
        <Button label="Clear" icon="pi pi-times-circle" size="small" severity="secondary" text
          @click="selectedRows = []" />
      </div>
    </div>

    <DataTable :value="courses" :loading="loading" size="small" stripedRows dataKey="id"
      v-model:selection="selectedRows"
      :paginator="true" :rows="perPage" :totalRecords="totalRecords" :lazy="true"
      :rowsPerPageOptions="[25, 50, 100]" @page="onPageChange">
      <template #empty>
        <div class="py-6 text-center text-gray-400 text-sm">
          {{ search ? 'No matching unfloated courses found.' : 'All courses are assigned to a curriculum. Nice!' }}
        </div>
      </template>
      <Column selectionMode="multiple" headerStyle="width: 3rem" />
      <Column field="course_code" header="Course Code" sortable class="font-medium" />
      <Column field="course_title" header="Course Title" sortable />
      <Column field="unit" header="Units" sortable style="width: 6rem">
        <template #body="{ data }">
          <span class="bg-gray-100 text-gray-700 px-2 py-0.5 rounded text-xs font-bold">{{ data.unit }}</span>
        </template>
      </Column>
      <Column field="course_instance_id" header="Course Instance" style="width: 12rem">
        <template #body="{ data }">
          <span class="text-xs text-gray-500">{{ formatCourseInstance(data) || '—' }}</span>
        </template>
      </Column>
    </DataTable>

    <BulkAddToCurriculumDialog v-model:visible="showBulkCurriculumDialog"
      :courses="selectedForCurriculum" @success="onCurriculumSuccess" />
  </div>
</template>
